import { useMsal } from "@azure/msal-react";
import { FileMetadata } from "../components/common/PymbleFileUploader";
import { launchExternal } from "../utils";
import { getStorageContainerJson, StorageEndpoint } from "./useAzureStorage";

import { usePymbleApi } from "./usePymbleApi";
import { useIonToast } from "@ionic/react";
import { useState } from "react";

// const FILE_STORAGE_END_POINT = `${api.myPymble}/filestorage`;
const FILE_STORAGE_END_POINT = "/storage/file/";
const FILE_EMAIL_END_POINT = `${FILE_STORAGE_END_POINT}email/`;

/**
 * Hook for file upload functions, state and component
 */
export function useFileUtils() {
  const { callApiS2: callApi } = usePymbleApi();
  const { instance /*, inProgress */ } = useMsal();
  const [showToast] = useIonToast();
  const [isSending, setIsSending] = useState(false);

  const deleteFile = async (container: string, fileId: string) => {
    if (container && fileId) {
      const response = await callApi(
        "DELETE",
        `${FILE_STORAGE_END_POINT}?container=${container}&id=${fileId}`
      );
      return response;
    } else {
      console.log("deleteFile: Invalid parameters");
      return null;
    }
  };

  const saveAndOpenFile = async (f: FileMetadata) => {
    try {
      if (f.Url) {
        console.debug("Opening file:", JSON.stringify(f));

        let fileUrl = f.Url;

        if (f?.Private) {
          // File is private, so we need access it using the authorised MyPymble API
          // with a token supplied by the previous API call, which is unique to this file and user
          // const private_file_endpoint = `${api.myPymble}/filestorage?container=${f?.Container}&id=${f?.InternalName}&token=${f?.Token}`;
          const private_file_endpoint = `/storage/file/?container=${f?.Container}&id=${f?.InternalName}&token=${f?.Token}`;
          try {
            const response = await callApi("GET", private_file_endpoint, {
              responseType: "blob",
            });

            if (!response) {
              throw new Error("No response from private file endpoint.");
            }

            // Create a blob from the response and download it
            // https://stackoverflow.com/questions/19327749/javascript-blob-filename-without-link
            const blob = new Blob([response], { type: f?.Type });


            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", f?.Name);
            document.body.appendChild(link);
            link.click();
            // Clean up the URL object
            URL.revokeObjectURL(url);
            // The following is not really necessary, because the link is not added to the
            // document tree and therefore does not create any memory leaks or other issues.
            // Once the click() method is called, the download will start and the a
            // element will be discarded automatically
            setTimeout(() => {
              link.remove();
            }, 1000);
          } catch (e: any) {
            await showToast({
              message: "Could not download attachment: " + e,
              color: "danger", position: "middle", duration: 4000
            });
            console.error("Could not download attachment.", e, e?.message);
          }
        } else if (f?.Secure) {
          // File is secured, so we need to get a SAS token
          // Get SAS query for the secure container
          const sasJson = await getStorageContainerJson(
            instance,
            StorageEndpoint.secure,
            f.Name,
            false
          );
          fileUrl += "?" + sasJson.SasQueryString;
          launchExternal(fileUrl);
        } else {
          // File is not secured, so we can just open it
          launchExternal(fileUrl);
        }
      } else {
        throw new Error("File document does not contain a URL string.");
      }
    } catch (e: any) {
      await showToast({
        message: "Oops. There was a problem downloading your attachment.",
        color: "danger",
        position: "middle",
        duration: 4000,
      });
      console.error("Could not download attachment.", e, e?.message);
    }
  };

  const emailFile = async (f: FileMetadata, email: string = "") => {
    // Send the file to the user's email address, determined by the token 
    // on the file object, which is unique to the user
    setIsSending(true);
    if (f?.Container && f?.InternalName) {
      const response = await callApi(
        "POST",
        FILE_EMAIL_END_POINT + `?email=${email}`, {
        data: f,
      }
      );
      setIsSending(false);
      return response;
    } else {
      console.log("emailFile: Invalid parameters");
      setIsSending(false);
      return null;
    }
  }

  return {
    deleteFile,
    emailFile,
    isSending,
    saveAndOpenFile,
  };
}
